import { Component, EventEmitter, Input, Output, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

export interface SiteMultiSelectOption {
  id: string;
  name: string;
  /** Non sélectionnable pour un NOUVEL ajout (ex. offre indisponible sur ce site) -- un site déjà sélectionné avant l'ouverture de l'écran reste toujours modifiable même s'il devient inéligible entre-temps (voir comm-prevention-form). */
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Sélecteur multi-sites (recherche + cases à cocher + compteur + tout sélectionner/désélectionner) --
 * remplace la simple liste statique cochée/décochée (Lot "Sites de diffusion modifiables",
 * 2026-09-09), pensé pour des organisations à 40-100+ sites où une liste de chips serait
 * inexploitable. Partagé création/édition (`comm-prevention-form`), jamais un second composant
 * dupliqué pour chaque mode.
 */
@Component({
  selector: 'app-site-multi-select',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './site-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./site-multi-select.component.scss'],
})
export class SiteMultiSelectComponent {
  @Input() sites: SiteMultiSelectOption[] = [];

  @Input()
  set selectedIds(v: Set<string> | null | undefined) {
    this._selectedIds = v ?? new Set();
  }
  get selectedIds(): Set<string> { return this._selectedIds; }

  @Output() selectedIdsChange = new EventEmitter<Set<string>>();

  private _selectedIds = new Set<string>();
  search = signal('');

  readonly filteredSites = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.sites;
    return this.sites.filter(s => s.name.toLowerCase().includes(q));
  });

  onSearchInput(value: string): void {
    this.search.set(value);
  }

  isSelected(id: string): boolean {
    return this._selectedIds.has(id);
  }

  toggle(site: SiteMultiSelectOption): void {
    if (site.disabled) return;
    const next = new Set(this._selectedIds);
    if (next.has(site.id)) next.delete(site.id);
    else next.add(site.id);
    this.selectedIdsChange.emit(next);
  }

  selectAll(): void {
    const next = new Set(this._selectedIds);
    for (const site of this.sites) {
      if (!site.disabled) next.add(site.id);
    }
    this.selectedIdsChange.emit(next);
  }

  deselectAll(): void {
    const next = new Set(this._selectedIds);
    for (const site of this.sites) {
      if (!site.disabled) next.delete(site.id);
    }
    this.selectedIdsChange.emit(next);
  }
}
